import { ArrowRight, Bot, Building2, ChevronDown, ChevronUp, LineChart, Sparkles, Trophy } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AgentPortalHeroDiscoveryTiles } from "@/components/agent/AgentPortalHeroDiscoveryTiles";
import { AgentLandingSkeleton } from "@/components/agent/AgentPortalSkeletons";
import { ShortlistActionButton } from "@/components/agent-shortlist/ShortlistActionButton";
import { ShortlistStudioPanel } from "@/components/agent-shortlist/ShortlistStudioPanel";
import { trackLeadIntent } from "@/lib/leadIntent";
import { usePortalShortlist } from "@/hooks/usePortalShortlist";
import { SearchBar } from "@/components/SearchBar";

const SIGNATURE_HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/4bc8a89a-e1c9-462b-87e6-ef6be029552e/images/205f84bcf8533df544eadc5f5d13962093a8c460d6b4d18309f166d4bfe362bc.png";
const SIGNATURE_AGENT_PORTRAIT =
  "https://images.unsplash.com/photo-1603084756639-e5f9a0d2123a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzc21hbiUyMHN1aXQlMjBwb3J0cmFpdCUyMGFzaWFufGVufDB8fHx8MTc3NjQyNTA4MHww&ixlib=rb-4.1.0&q=85";
const DISTRICT_CODES = Array.from({ length: 28 }, (_, index) => `D${String(index + 1).padStart(2, "0")}`);
const SIGNATURE_ADVISOR_FEATURES = [
  { title: "Guided District Discovery", body: "Move from broad segments to a specific shortlist through a data-driven neighborhood discovery flow." },
  { title: "2026 ABSD/BSD Calculator", body: "Instant clarity on current tax regulations—know your exact entry costs based on your residency and profile." },
  { title: "Rent vs. Buy Analysis", body: "A sophisticated financial model comparing the long-term cost of waiting versus the equity of ownership." },
  { title: "Docked Side-by-Side Viewing", body: "Review project results in a dedicated side-panel without losing the thread of your research." },
  { title: "Intelligent Alternatives", body: "Discover hidden gems and adjacent projects that offer better value within your preferred districts." },
  { title: "Structured Comparisons", body: "Evaluate projects side-by-side with hard data to ensure your final choice is objective and data-backed." },
  { title: "Gated PDF Report Delivery", body: "Receive professional market memos and property reports delivered instantly as high-value digital assets." },
  { title: "Integrated Viewing Requests", body: "Skip the manual back-and-forth; select dates and book private viewings directly through the interface." },
  { title: "Contextual Listing Support", body: "The Advisor automatically recognizes which property you are viewing and prepares relevant data instantly." },
  { title: "Direct WhatsApp Handoff", body: "Transition seamlessly from digital research to a direct handshake with your Signature Agent." },
  { title: "Adaptive Memory Profile", body: "The Advisor remembers your budget, timeline, and goals, ensuring every interaction builds on the last." },
  { title: "AI Comparison Strategist", body: "Highlights the specific competitive angles and unique strengths of the properties on your shortlist." },
  { title: "Shortlist Curation", body: "Intelligent suggestions for your Primary Pick, Backup Option, and Market Watchout." },
  { title: "Investor Thesis Engine", body: "Access institutional-grade cards covering the upside potential, risk factors, and exit strategies for any unit." },
  { title: "District Intelligence Narratives", body: "Understand the demand catalysts and market shifts in specific districts through expert-level narratives." },
  { title: "Objection-Handling Intelligence", body: "Get instant, data-backed counters to market hesitations and common buyer concerns." },
  { title: "Investment Memo Generation", body: "Receive a structured summary of your search and strategy, tailored for high-stakes decision-making." },
  { title: "Scenario Stress-Testing", body: "Simulate Defensive, Stretch, or Growth horizons to see how your investment holds over time." },
  { title: "Concierge-Ready Briefing", body: "Your agent is briefed on your specific needs before you even speak, ensuring every conversation is high-value." },
];

const SIGNATURE_LISTING_PAGE_FEATURES = [
  { title: "Future Profit Forecast", body: "See a simple prediction of how much this specific home could be worth in the future." },
  { title: "Professional Property Summary", body: "Instantly create a clear, one-page report that breaks down why this home is a good or bad buy." },
  { title: "Easy PDF Downloads", body: "Save your property reports as PDFs to read later or share with family." },
  { title: "It Knows What You’re Looking At", body: "Our AI assistant automatically sees which home you are viewing, so you do not have to type in the details." },
  { title: "Instant Help Button", body: "Click Explore Now to start chatting and getting answers right on the listing page." },
  { title: "Side-by-Side Chatting", body: "The assistant stays in a small window on the side so you can chat while still looking at the listing photos." },
  { title: "People Also Liked Suggestions", body: "Find other homes nearby that match your budget and style more closely." },
  { title: "Compare Homes Easily", body: "Pick two or three homes and see exactly how they stack up against each other." },
  { title: "Save Your Favorites", body: "Keep a shortlist of homes you love and get advice shaped around that specific group." },
  { title: "Neighborhood Insider", body: "Get the inside scoop on the area, including what is being built nearby and why the location matters." },
  { title: "Expert Buy Strategy", body: "See the pros, cons, and risks of a specific home before you commit." },
  { title: "Honest Answers to Your Worries", body: "Concerned about the price or the market? Ask the AI and get a data-backed answer instantly." },
  { title: "What-If Planning", body: "See how your investment could handle different situations, such as interest-rate changes or market shifts." },
  { title: "Book a Tour Instantly", body: "Schedule a time to see the home in person directly through the chat." },
  { title: "One-Click WhatsApp", body: "Whenever you are ready, jump straight into a WhatsApp chat with a real human agent." },
  { title: "No-Waste-of-Time Briefing", body: "Before you talk to an agent, the system sends them a summary of what you are looking for so they are already prepared." },
  { title: "Helpful Sidebar", body: "All these smart tools live in a simple panel on the side of the page, always there when you need them." },
  { title: "The Real Numbers", body: "See the actual math and data behind the price tag so you know you are not overpaying." },
];

const average = (values = []) => {
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const extractFirstNumeric = (listing, keys = []) => {
  for (const key of keys) {
    const value = Number(listing?.[key]);
    if (Number.isFinite(value) && value > 0) return value;
  }
  return null;
};

const formatPct = (value) => (value == null ? null : `${value.toFixed(1)}%`);

const formatAlphaScore = (value) => (value == null ? "—" : value.toFixed(1));

const buildDistrictAlphaScore = (district) => {
  const averageYield = Number(district?.averageYield ?? 0);
  const averageGap = Math.max(Number(district?.averageGap ?? 0), 0);
  const underValuedCount = Number(district?.underValuedCount ?? 0);
  const listingCount = Number(district?.listingCount ?? 0);
  const topAlphaScore = Number(district?.topAlphaScore ?? 0);
  const score = averageYield * 11.5 + averageGap * 1.8 + Math.min(underValuedCount, 10) * 2.6 + Math.min(listingCount, 18) * 0.45 + topAlphaScore * 0.38;
  return Number.isFinite(score) ? Math.min(score, 99).toFixed(1) : "0.0";
};


const buildAlphaLogicStats = (listings = [], advisorFirstName = "Advisor", portfolioMetrics = null) => {
  const marketYieldValues = listings
    .map((listing) =>
      extractFirstNumeric(listing, [
        "district_average_yield_pct",
        "market_average_yield_pct",
        "avg_market_yield_pct",
        "district_avg_yield_pct",
      ]),
    )
    .filter((value) => value != null);
  const portfolioYieldValues = listings
    .map((listing) => extractFirstNumeric(listing, ["estimated_rental_yield_pct", "rental_yield_pct", "gross_yield_pct", "yield_pct"]))
    .filter((value) => value != null);
  const marketAverageYield = portfolioMetrics?.market_average_yield_pct ?? average(marketYieldValues);
  const portfolioYield = portfolioMetrics?.portfolio_yield_pct ?? average(portfolioYieldValues);
  const underValuedUnits = portfolioMetrics?.under_valued_unit_count ?? listings.filter((listing) => Number(listing?.price_gap_pct || 0) > 10).length;
  const portfolioListingCount = portfolioMetrics?.listing_count ?? listings.length;

  const yieldSpread = marketAverageYield != null && portfolioYield != null ? portfolioYield - marketAverageYield : null;

  return [
    {
      icon: LineChart,
      label: "Area Rental Yield",
      value: formatPct(marketAverageYield) || "Checking nearby trends...",
      detail: marketAverageYield != null ? "A live rental benchmark based on nearby portfolio comps." : "Waiting for synced comparables to establish a clean district baseline.",
    },
    {
      icon: Sparkles,
      label: `${advisorFirstName}'s Portfolio Yield`,
      value: formatPct(portfolioYield) || "Refreshing now...",
      detail:
        yieldSpread != null
          ? `${yieldSpread >= 0 ? "+" : ""}${yieldSpread.toFixed(1)} pts versus the nearby average.`
          : "Live rental-yield comparisons appear automatically once the latest listing data is synced.",
    },
    {
      icon: Building2,
      label: "Buyer Watchlist",
      value: portfolioListingCount ? String(portfolioListingCount) : "Refreshing homes...",
      detail: portfolioListingCount ? `${underValuedUnits} homes currently stand out on price-gap screening across ${portfolioListingCount} live listings.` : "Live portfolio counts will appear the moment inventory lands.",
    },
  ];
};

const SignatureEditorialListingCard = ({ advisorFirstName, buildBrandedPath, getWhatsAppHref, leadFormPath, listing }) => {
  const image = listing.image_url || listing.gallery_urls?.[0] || "";
  const isAlphaPick = Number(listing?.price_gap_pct || 0) > 10;

  return (
    <article className="group grid gap-4" data-testid={`signature-editorial-card-${listing.id}`}>
      <Link className="relative block overflow-hidden rounded-[34px] bg-[#ECE6DA] shadow-[0_24px_70px_rgba(44,44,44,0.08)]" data-testid={`signature-editorial-image-link-${listing.id}`} to={buildBrandedPath(`/listings/${listing.id}`)}>
        {image ? (
          <img alt={listing.title} className="aspect-[1.18/1] w-full object-cover transition duration-700 ease-out group-hover:scale-105" data-testid={`signature-editorial-image-${listing.id}`} decoding="async" loading="lazy" src={image} />
        ) : (
          <div className="flex aspect-[1.18/1] items-center justify-center bg-[linear-gradient(135deg,#e7e1d6_0%,#f7f3ea_100%)] text-sm font-medium text-[#2C2C2C]" data-testid={`signature-editorial-image-fallback-${listing.id}`}>
            Signature visual pending
          </div>
        )}
        {isAlphaPick ? (
          <span className="absolute left-5 top-5 inline-flex items-center rounded-full bg-[#D4AF37] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#2C2C2C]" data-testid={`signature-editorial-alpha-pick-${listing.id}`}>
            Featured Match
          </span>
        ) : null}
      </Link>

      <div className="space-y-4 px-1">
        <div className="space-y-2">
          <p className="signature-kicker-label text-[11px] font-bold uppercase tracking-[0.24em] text-[#8B6E3D]" data-testid={`signature-editorial-district-${listing.id}`}>
            {listing.district} • {listing.property_type}
          </p>
          <Link className="inline-block" data-testid={`signature-editorial-title-link-${listing.id}`} to={buildBrandedPath(`/listings/${listing.id}`)}>
            <h3 className="font-[Cormorant_Garamond,serif] text-4xl font-bold leading-none text-[#2C2C2C]" data-testid={`signature-editorial-title-${listing.id}`}>
              {listing.title}
            </h3>
          </Link>
          <p className="text-sm leading-7 text-[#58544B]" data-testid={`signature-editorial-subcopy-${listing.id}`}>
            {listing.address}
            {Number(listing?.price_gap_pct || 0) > 0 ? ` · ${listing.price_gap_pct.toFixed(1)}% below nearby benchmark` : " · Selected for strong fit and current market context"}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4 text-sm font-medium text-[#2C2C2C]">
          <span data-testid={`signature-editorial-price-${listing.id}`}>{listing.price ? `$${Number(listing.price).toLocaleString()}` : "Price on request"}</span>
          <span data-testid={`signature-editorial-size-${listing.id}`}>{listing.sqft ? `${Number(listing.sqft).toLocaleString()} sqft` : "Size upon request"}</span>
        </div>

        <div className="flex flex-wrap gap-3">
          <ShortlistActionButton listing={listing} testId={`signature-editorial-shortlist-${listing.id}`} variant="soft" />
          <a className="gold-button" data-testid={`signature-editorial-whatsapp-${listing.id}`} href={getWhatsAppHref(`Hello ${advisorFirstName}, I would like more details about ${listing.title}.`)} onClick={() => void trackLeadIntent({ eventType: "whatsapp_enquire_click", listing, metadata: { source: "signature_editorial_whatsapp" } })} rel="noreferrer" target="_blank">
            WhatsApp {advisorFirstName}
          </a>
          <Link className="outline-button" data-testid={`signature-editorial-lead-form-${listing.id}`} to={leadFormPath}>
            Request Details
          </Link>
        </div>
      </div>
    </article>
  );
};

export const SignatureAgentLandingPage = ({ branding, buildBrandedPath, districtIntelligenceFeed = [], filters, getWhatsAppHref, handleSearch, isLoading = false, leadFormPath, listings, onFiltersChange, onResetFilters, options, portfolioMetrics = null }) => {
  const [hoveredDistrict, setHoveredDistrict] = useState("");
  const [isSignalPanelOpen, setIsSignalPanelOpen] = useState(false);
  const [isCompactFeatureSections, setIsCompactFeatureSections] = useState(false);
  const [isListingToolsExpanded, setIsListingToolsExpanded] = useState(true);
  const [isAdvisorFeaturesExpanded, setIsAdvisorFeaturesExpanded] = useState(true);
  const shortlist = usePortalShortlist();
  const advisorFirstName = String((branding.name || "Advisor").split(" ")[0] || "Advisor");
  const alphaLogicStats = buildAlphaLogicStats(listings, advisorFirstName, portfolioMetrics);
  const portraitImage = branding.profile_photo_url || SIGNATURE_AGENT_PORTRAIT;
  const activeDistrict = filters.district || "";
  const quickRoutes = useMemo(
    () => ([
      {
        title: "Buy a home",
        body: "Browse move-in ready homes, compare nearby options, and shortlist favourites before you speak with the team.",
        cta: "Explore homes",
        href: buildBrandedPath("/buy"),
        testId: "signature-quick-route-buy",
      },
      {
        title: "Rent with clarity",
        body: "Review current rental options, neighbourhood signals, and practical next steps in one cleaner search flow.",
        cta: "See rentals",
        href: buildBrandedPath("/rent"),
        testId: "signature-quick-route-rent",
      },
      {
        title: "Sell with guidance",
        body: `Speak with ${advisorFirstName} about pricing, positioning, and when to list for the strongest response.`,
        cta: "Request advice",
        href: buildBrandedPath("/contact"),
        testId: "signature-quick-route-sell",
      },
    ]),
    [advisorFirstName, buildBrandedPath],
  );
  const portalHelpFeatures = useMemo(() => ([...SIGNATURE_LISTING_PAGE_FEATURES.slice(0, 3), ...SIGNATURE_ADVISOR_FEATURES.slice(0, 3)]), []);

  const districtIntelligence = useMemo(() => {
    if (districtIntelligenceFeed.length) {
      return DISTRICT_CODES.map((districtCode) => {
        const intelligence = districtIntelligenceFeed.find((item) => item.district_code === districtCode);
        const averageYield = intelligence?.average_yield_pct ?? null;
        const averageGap = intelligence?.average_price_gap_pct ?? null;
        const underValuedCount = intelligence?.under_valued_count ?? 0;
        return {
          districtCode,
          averageYield,
          averageGap,
          underValuedCount,
          listingCount: intelligence?.listing_count ?? 0,
          topAlphaScore: intelligence?.top_alpha_score ?? null,
          districtAlphaScore: intelligence?.district_alpha_score ?? null,
          hint:
            averageYield != null
              ? `${districtCode}: Avg yield ${averageYield.toFixed(1)}% | Price gap ${averageGap != null ? `${averageGap >= 0 ? "+" : ""}${averageGap.toFixed(1)}%` : "—"} | ${underValuedCount} standout homes.`
              : `${districtCode}: Waiting for fresh district inventory.`,
        };
      });
    }

    return DISTRICT_CODES.map((districtCode) => {
      const districtListings = listings.filter((listing) => listing.district === districtCode);
      const districtYieldValues = districtListings
        .map((listing) => extractFirstNumeric(listing, ["estimated_rental_yield_pct", "rental_yield_pct", "gross_yield_pct", "yield_pct"]))
        .filter((value) => value != null);
      const averageYield = average(districtYieldValues);
      const averageGap = average(districtListings.map((listing) => Number(listing?.price_gap_pct || listing?.value_gap_percentage || 0)));
      const underValuedCount = districtListings.filter((listing) => Number(listing?.price_gap_pct || listing?.value_gap_percentage || 0) > 10).length;
      return {
        districtCode,
        averageYield,
        averageGap,
        underValuedCount,
          topAlphaScore: null,
          districtAlphaScore: null,
        hint:
          averageYield != null
            ? `${districtCode}: Avg yield ${averageYield.toFixed(1)}% | Price gap ${averageGap != null ? `${averageGap >= 0 ? "+" : ""}${averageGap.toFixed(1)}%` : "—"} | ${underValuedCount} standout homes.`
            : `${districtCode}: Yield data syncing...`,
        listingCount: districtListings.length,
      };
    });
  }, [districtIntelligenceFeed, listings]);

  const districtLeaderboard = useMemo(() => {
    return districtIntelligence
      .filter((district) => district.listingCount > 0)
      .map((district) => ({
        ...district,
        resolvedDistrictAlphaScore: district.districtAlphaScore ?? Number(buildDistrictAlphaScore(district)),
      }))
      .sort((left, right) => right.resolvedDistrictAlphaScore - left.resolvedDistrictAlphaScore)
      .slice(0, 5);
  }, [districtIntelligence]);

  const filteredEditorialListings = useMemo(() => {
    return listings.filter((listing) => {
      if (filters.district && listing.district !== filters.district) return false;
      if (filters.tenure && listing.tenure !== filters.tenure) return false;
      if (filters.property_type && listing.property_type !== filters.property_type) return false;
      return true;
    });
  }, [filters.district, filters.property_type, filters.tenure, listings]);

  const alphaSignalState = useMemo(() => {
    const selectedDistrict = activeDistrict ? districtIntelligence.find((item) => item.districtCode === activeDistrict) : null;
    const topGrowthDistrict = districtLeaderboard[0] || null;
    const listingCount = listings.length;
    const agentName = String((branding.name || "Advisor").split(" ")[0] || "Advisor").toUpperCase();

    if (selectedDistrict && selectedDistrict.listingCount > 0) {
      return {
        districtCode: selectedDistrict.districtCode,
        message: `DISTRICT SNAPSHOT: ${selectedDistrict.districtCode} AVG YIELD ${(selectedDistrict.averageYield ?? 0).toFixed(1)}% | ${selectedDistrict.underValuedCount} STANDOUT HOMES FOUND.`,
      };
    }

    if (topGrowthDistrict) {
      return {
        districtCode: topGrowthDistrict.districtCode,
        message: `DISTRICT SNAPSHOT: ACROSS ${agentName}'S ${listingCount} LISTINGS, ${topGrowthDistrict.districtCode} IS THE STRONGEST AREA TO REVIEW NEXT.`,
      };
    }

    return {
      districtCode: null,
      message: "DISTRICT SNAPSHOT: LIVE MARKET DATA IS SCANNING THE PORTFOLIO.",
    };
  }, [activeDistrict, branding.name, districtIntelligence, districtLeaderboard, listings.length]);

  const alphaHintCopy = alphaSignalState.message;
  const highlightedDistrictCode = alphaSignalState.districtCode;
  const selectedDistrictDetail = activeDistrict ? districtIntelligence.find((item) => item.districtCode === activeDistrict) : null;

  const signalInsightCopy = useMemo(() => {
    if (!selectedDistrictDetail || selectedDistrictDetail.listingCount <= 0) {
      return "Select a district above to open its current yield, pricing gap, and overall watchlist score.";
    }
    const alphaScore = Number(selectedDistrictDetail.districtAlphaScore ?? selectedDistrictDetail.resolvedDistrictAlphaScore ?? buildDistrictAlphaScore(selectedDistrictDetail));
    const avgGap = selectedDistrictDetail.averageGap ?? 0;
    if (selectedDistrictDetail.underValuedCount > 0) {
      return `${selectedDistrictDetail.districtCode} is looking strong right now, with ${selectedDistrictDetail.underValuedCount} standout homes and a watchlist score of ${alphaScore.toFixed(1)} supported by a ${avgGap >= 0 ? "+" : ""}${avgGap.toFixed(1)}% average pricing gap.`;
    }
    return `${selectedDistrictDetail.districtCode} is holding a steady watchlist position with a score of ${alphaScore.toFixed(1)}, an average price gap of ${avgGap >= 0 ? "+" : ""}${avgGap.toFixed(1)}%, and live yield support from ${selectedDistrictDetail.listingCount} active listings.`;
  }, [selectedDistrictDetail]);

  useEffect(() => {
    if (activeDistrict) {
      setIsSignalPanelOpen(true);
      return;
    }
    setIsSignalPanelOpen(false);
  }, [activeDistrict]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1440px)");
    const handleFeatureSectionMode = (event) => {
      const compact = event.matches;
      setIsCompactFeatureSections(compact);
      setIsListingToolsExpanded(!compact);
      setIsAdvisorFeaturesExpanded(!compact);
    };

    handleFeatureSectionMode(mediaQuery);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleFeatureSectionMode);
      return () => mediaQuery.removeEventListener("change", handleFeatureSectionMode);
    }

    mediaQuery.addListener(handleFeatureSectionMode);
    return () => mediaQuery.removeListener(handleFeatureSectionMode);
  }, []);

  const handleDistrictSelect = (districtCode) => {
    onFiltersChange((current) => ({ ...current, district: current.district === districtCode ? "" : districtCode }));
  };

  if (isLoading) {
    return <AgentLandingSkeleton />;
  }

  return (
    <div className="bg-transparent" data-testid="signature-agent-landing-page">
      <section className="relative isolate overflow-hidden" data-testid="signature-hero-section">
        <div className="absolute inset-0">
          <img alt="Luxury Singapore penthouse skyline" className="h-full max-h-[84vh] w-full object-cover" data-testid="signature-hero-background-image" decoding="async" loading="lazy" src={SIGNATURE_HERO_IMAGE} />
          <div className="absolute inset-0 bg-[linear-gradient(105deg,rgba(22,22,22,0.86)_0%,rgba(22,22,22,0.58)_40%,rgba(22,22,22,0.18)_74%)]" />
        </div>

        <div className="section-shell relative z-10 flex min-h-[78vh] items-end py-16 lg:py-24">
          <div className="grid w-full gap-10 xl:grid-cols-[1.15fr_0.85fr] xl:items-end">
            <div className="max-w-4xl text-[#F9F8F6]" data-testid="signature-hero-copy-block">
              <p className="signature-kicker-label text-xs font-bold uppercase tracking-[0.32em] text-[#D4AF37]">Singapore property guidance</p>
              <h1 className="mt-6 font-[Cormorant_Garamond,serif] text-5xl font-[800] leading-[0.95] text-white [text-shadow:0_18px_45px_rgba(0,0,0,0.35)] sm:text-6xl lg:text-7xl" data-testid="signature-hero-heading">
                Find the right home with a real advisor beside you.
              </h1>
              <p className="mt-6 max-w-3xl text-base leading-8 text-[#F6F1E9] [text-shadow:0_8px_28px_rgba(0,0,0,0.3)] sm:text-lg" data-testid="signature-hero-subheading">
                Browse curated listings, compare nearby opportunities, and move from shortlist to viewing with {branding.name} in one premium portal.
              </p>
              <div className="mt-8 flex flex-wrap gap-3" data-testid="signature-hero-trust-pill-row">
                <span className="rounded-full border border-[#D4AF37]/35 bg-black/20 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#F6F1E9]" data-testid="signature-hero-verified-pill">Verified advisor</span>
                {branding.cea_registration_number ? <span className="rounded-full border border-[#D4AF37]/35 bg-black/20 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#F6F1E9]" data-testid="signature-hero-cea-pill">CEA {branding.cea_registration_number}</span> : null}
                {branding.parent_agency_name ? <span className="rounded-full border border-[#D4AF37]/35 bg-black/20 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#F6F1E9]" data-testid="signature-hero-agency-pill">{branding.parent_agency_name}</span> : null}
                {branding.specialties?.[0] ? <span className="rounded-full border border-[#D4AF37]/35 bg-black/20 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#F6F1E9]" data-testid="signature-hero-specialty-pill">{branding.specialties[0]}</span> : null}
              </div>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link className="gold-button" data-testid="signature-hero-browse-button" to={buildBrandedPath("/listings")}>Browse listings <ArrowRight className="ml-2 h-4 w-4" /></Link>
                <a className="gold-button" data-testid="signature-hero-whatsapp-button" href={getWhatsAppHref(`Hello ${advisorFirstName}, I would like help with my property search.`)} rel="noreferrer" target="_blank">WhatsApp {advisorFirstName}</a>
                <Link className="outline-button !border-white/25 !text-white hover:!bg-white/10" data-testid="signature-hero-contact-button" to={leadFormPath}>Request a callback</Link>
              </div>

              <AgentPortalHeroDiscoveryTiles buildBrandedPath={buildBrandedPath} surface="dark" testIdPrefix="signature-agent-hero-discovery" />
            </div>

            <div className="w-full xl:justify-self-end" data-testid="signature-profile-card-wrapper">
              <div className="w-full max-w-[30rem] rounded-[32px] border border-[#D4AF37]/28 bg-white/10 p-6 shadow-[0_30px_90px_rgba(0,0,0,0.22)] backdrop-blur-xl sm:p-8" data-testid="signature-profile-card">
                <p className="signature-kicker-label text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37]">Meet your advisor</p>
                <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center">
                  <img alt={`${branding.name} portrait`} className="h-20 w-20 rounded-full border border-white/20 object-cover shadow-[0_16px_32px_rgba(0,0,0,0.18)]" data-testid="signature-profile-photo" src={portraitImage} />
                  <div>
                    <p className="font-[Cormorant_Garamond,serif] text-4xl font-bold leading-none text-white" data-testid="signature-profile-name">{branding.name}</p>
                    <p className="mt-2 text-sm uppercase tracking-[0.22em] text-[#F2ECE3]" data-testid="signature-profile-role">Singapore property advisor</p>
                    {branding.advisor_tagline ? <p className="mt-3 text-sm leading-7 text-[#F2ECE3]" data-testid="signature-profile-tagline">{branding.advisor_tagline}</p> : null}
                    {branding.verified_badge_label ? <span className="mt-3 inline-flex rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/18 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#F6F1E9]" data-testid="signature-profile-verified-badge">{branding.verified_badge_label}</span> : null}
                    {branding.cea_registration_number ? <p className="mt-3 text-xs uppercase tracking-[0.18em] text-[#D4AF37]" data-testid="signature-profile-cea">CEA {branding.cea_registration_number}</p> : null}
                    {branding.parent_agency_name ? <p className="mt-2 text-xs uppercase tracking-[0.18em] text-[#F2ECE3]" data-testid="signature-profile-agency-name">{branding.parent_agency_name}</p> : null}
                    {branding.agency_license_number ? <p className="mt-2 text-xs uppercase tracking-[0.18em] text-[#F2ECE3]" data-testid="signature-profile-agency-license">Agency licence {branding.agency_license_number}</p> : null}
                    {branding.service_regions?.length ? <p className="mt-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]" data-testid="signature-profile-service-regions">{branding.service_regions.join(" • ")}</p> : null}
                  </div>
                </div>
                <div className="mt-6 rounded-[22px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))] px-4 py-4 sm:px-5" data-testid="signature-profile-bio-block">
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]" data-testid="signature-profile-bio-label">Expertise</p>
                  <p className="mt-3 text-sm leading-7 text-[#FFF7EA]" data-testid="signature-profile-copy">
                    {branding.advisor_bio || "Practical guidance for buyers, sellers, and families who want clear next steps—not a hard sell."}
                  </p>
                </div>
                <div className="mt-6 grid gap-3 sm:grid-cols-2" data-testid="signature-profile-contact-grid">
                  <a className="gold-button !w-full !justify-center" data-testid="signature-profile-whatsapp-button" href={getWhatsAppHref(`Hello ${advisorFirstName}, I would like to speak about a property.`)} rel="noreferrer" target="_blank">WhatsApp</a>
                  <Link className="outline-button !w-full !justify-center !border-white/20 !text-white hover:!bg-white/10" data-testid="signature-profile-contact-link" to={leadFormPath}>Contact</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell relative z-10 -mt-4 pb-2 pt-0" data-testid="signature-editorial-transition-section">
        <div className="mx-auto h-16 max-w-6xl overflow-hidden rounded-[999px] border border-[#D4AF37]/12 bg-[linear-gradient(180deg,rgba(242,237,228,0.82)_0%,rgba(232,224,213,0.96)_100%)] shadow-[0_16px_45px_rgba(90,75,53,0.08)]">
          <div className="h-full w-full bg-[radial-gradient(circle_at_10%_50%,rgba(212,175,55,0.16),transparent_16%),radial-gradient(circle_at_88%_50%,rgba(26,26,26,0.06),transparent_18%),linear-gradient(90deg,rgba(255,255,255,0)_0%,rgba(255,255,255,0.42)_48%,rgba(255,255,255,0)_100%)]" />
        </div>
      </section>

      <section className="section-shell relative z-20 -mt-6 pb-4 pt-0" data-testid="signature-agent-trust-section">
        <section className="rounded-[32px] border border-[#D4AF37]/20 bg-[linear-gradient(135deg,#fff9ef_0%,#f7efe2_56%,#efe2c8_100%)] p-6 shadow-[0_18px_50px_rgba(90,75,53,0.08)] md:p-7" data-testid="signature-agent-trust-panel">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-3xl">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#8B6E3D]">A clearer client journey</p>
              <h2 className="mt-3 font-[Cormorant_Garamond,serif] text-3xl font-bold leading-none text-[#2C2C2C]" data-testid="signature-agent-trust-heading">
                Start where you are: buy, rent, or get advice before you decide.
              </h2>
              <p className="mt-3 text-sm leading-7 text-[#5D584F]" data-testid="signature-agent-trust-copy">
                This portal is built to make property decisions feel simpler—clear identity, live homes, shortlist tools, and a direct contact path when you are ready.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <a className="gold-button !px-4 !py-2 !text-xs !tracking-[0.16em]" data-testid="signature-agent-trust-contact-button" href={`mailto:${branding?.email || "hello@sgpropportal.sg"}`}>
                Contact {advisorFirstName}
              </a>
              <Link className="outline-button !px-4 !py-2 !text-xs !tracking-[0.16em]" data-testid="signature-agent-trust-listings-button" to={buildBrandedPath("/listings")}>
                View listings
              </Link>
            </div>
          </div>

          <div className="mt-7 grid gap-4 lg:grid-cols-3" data-testid="signature-quick-routes-grid">
            {quickRoutes.map((route) => (
              <Link className="rounded-[26px] border border-[#D4AF37]/16 bg-white/82 px-5 py-5 shadow-[0_16px_40px_rgba(90,75,53,0.05)] transition hover:-translate-y-1 hover:border-[#D4AF37]/40" data-testid={route.testId} key={route.title} to={route.href}>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8B6E3D]">Quick route</p>
                <h3 className="mt-3 text-lg font-semibold text-[#1f1a11]">{route.title}</h3>
                <p className="mt-3 text-sm leading-7 text-[#5D584F]">{route.body}</p>
                <span className="mt-5 inline-flex items-center text-sm font-semibold text-[#1f1a11]">{route.cta} <ArrowRight className="ml-2 h-4 w-4 text-[#8B6E3D]" /></span>
              </Link>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-[#D4AF37]/14 pt-7" data-testid="signature-agent-trust-advisor-features-heading-row">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#8B6E3D]" data-testid="signature-agent-trust-advisor-features-kicker">Helpful extras</p>
              <h3 className="mt-2 text-xl font-semibold text-[#1f1a11]" data-testid="signature-agent-trust-advisor-features-heading">What this portal helps you do faster</h3>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-[#D4AF37]/18 bg-white/80 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#8B6E3D]" data-testid="signature-agent-trust-advisor-features-count">
                6 client tools
              </span>
              {isCompactFeatureSections ? (
                <button className="outline-button !px-4 !py-2 !text-xs" data-testid="signature-agent-trust-advisor-features-toggle" onClick={() => setIsAdvisorFeaturesExpanded((current) => !current)} type="button">
                  {isAdvisorFeaturesExpanded ? <ChevronUp className="mr-2 h-4 w-4" /> : <ChevronDown className="mr-2 h-4 w-4" />}
                  {isAdvisorFeaturesExpanded ? "Collapse" : "Expand"}
                </button>
              ) : null}
            </div>
          </div>

          {!isCompactFeatureSections || isAdvisorFeaturesExpanded ? (
            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="signature-agent-trust-features-grid">
              {portalHelpFeatures.map((feature, index) => (
                <div className="rounded-[24px] border border-[#D4AF37]/16 bg-white/82 px-5 py-5 shadow-[0_16px_40px_rgba(90,75,53,0.05)]" data-testid={`signature-agent-trust-feature-${index + 1}`} key={feature.title}>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8B6E3D]">Tool {String(index + 1).padStart(2, "0")}</p>
                  <h3 className="mt-3 text-lg font-semibold text-[#1f1a11]">{feature.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-[#5D584F]">{feature.body}</p>
                </div>
              ))}
            </div>
          ) : null}
        </section>
      </section>

      <section className="section-shell py-6" data-testid="signature-district-hub-section">
        <div className="agent-paper-section rounded-[34px] px-6 py-8 md:px-8">
          <div className="grid gap-8 xl:grid-cols-[1.15fr_0.85fr] xl:items-start">
            <div>
              <p className="signature-kicker-label text-xs font-bold uppercase tracking-[0.28em] text-[#8B6E3D]">Neighbourhood guide</p>
              <h2 className="mt-3 font-[Cormorant_Garamond,serif] text-4xl font-bold leading-none text-[#2C2C2C] sm:text-5xl" data-testid="signature-district-hub-heading">
                Explore the 28 Districts
              </h2>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-[#5D584F]">Use district filters to narrow your shortlist quickly, see current rental yield ranges, and focus on the areas that match your goals.</p>
              {activeDistrict ? (
                <button className="outline-button mt-5" data-testid="signature-district-clear-button" onClick={() => handleDistrictSelect(activeDistrict)} type="button">
                  Clear {activeDistrict}
                </button>
              ) : null}

              <div className="mt-8 grid gap-3 sm:grid-cols-4 lg:grid-cols-7" data-testid="signature-district-grid">
                {districtIntelligence.map((district) => (
                  <button
                    className={`signature-district-chip rounded-full px-4 py-3 text-sm font-semibold transition ${activeDistrict === district.districtCode ? "bg-[#D4AF37] text-[#2C2C2C] shadow-[0_12px_28px_rgba(212,175,55,0.16)]" : "bg-white text-[#2C2C2C]"}`}
                    data-testid={`signature-district-chip-${district.districtCode.toLowerCase()}`}
                    key={district.districtCode}
                    onBlur={() => setHoveredDistrict("")}
                    onClick={() => handleDistrictSelect(district.districtCode)}
                    onFocus={() => setHoveredDistrict(district.districtCode)}
                    onMouseEnter={() => setHoveredDistrict(district.districtCode)}
                    onMouseLeave={() => setHoveredDistrict("")}
                    type="button"
                  >
                    {district.districtCode}
                  </button>
                ))}
              </div>

              <div
                aria-expanded={isSignalPanelOpen}
                className="signature-alpha-signal-bar mt-6 w-full cursor-pointer rounded-[28px] px-5 py-5 text-left transition hover:-translate-y-0.5 sm:px-6 sm:py-6"
                data-testid="signature-district-signal-bar"
                onClick={() => setIsSignalPanelOpen((current) => !current)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setIsSignalPanelOpen((current) => !current);
                  }
                }}
                role="button"
                style={{ color: "#F6E7B7", WebkitTextFillColor: "#F6E7B7" }}
                tabIndex={0}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="signature-alpha-signal-content flex items-center gap-4" key={alphaHintCopy}>
                    <span className="signature-live-pulse-dot h-3.5 w-3.5 rounded-full bg-[#D4AF37]" data-testid="signature-district-signal-pulse-dot" />
                    <p className="signature-alpha-signal-text text-[1.1rem] font-bold uppercase tracking-[0.05em] sm:text-[1.15rem] md:text-[1.2rem]" data-testid="signature-district-alpha-hint" style={{ color: "#F6E7B7", WebkitTextFillColor: "#F6E7B7", textShadow: "0 4px 18px rgba(0, 0, 0, 0.24), 0 0 18px rgba(212, 175, 55, 0.12)" }}>
                      {alphaHintCopy}
                    </p>
                  </div>
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/20 bg-white/5 text-[#D4AF37]" data-testid="signature-district-signal-expand-icon">
                    {isSignalPanelOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </span>
                </div>
              </div>
              <p className="mt-3 text-xs italic tracking-[0.04em] text-[#5D584F]/90" data-testid="signature-district-signal-instruction-note">
                Click any district to see the latest market snapshot.
              </p>
              {isSignalPanelOpen ? (
                <div className="signature-signal-detail-panel mt-4 rounded-[28px] border border-[#D4AF37]/20 bg-[#1E1E1E]/86 px-5 py-5 backdrop-blur-xl" data-testid="signature-district-signal-detail-panel">
                  {selectedDistrictDetail && selectedDistrictDetail.listingCount > 0 ? (
                    <>
                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="rounded-[22px] border border-white/10 bg-[#151515] px-4 py-4" data-testid="signature-district-signal-metric-yield">
                          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]">Average Yield</p>
                          <p className="mt-3 text-3xl font-semibold text-[#F9F8F6]">{formatPct(selectedDistrictDetail.averageYield) || "—"}</p>
                        </div>
                        <div className="rounded-[22px] border border-white/10 bg-[#151515] px-4 py-4" data-testid="signature-district-signal-metric-gap">
                          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]">Average Price Gap</p>
                          <p className="mt-3 text-3xl font-semibold text-[#F9F8F6]">{selectedDistrictDetail.averageGap != null ? `${selectedDistrictDetail.averageGap >= 0 ? "+" : ""}${selectedDistrictDetail.averageGap.toFixed(1)}%` : "—"}</p>
                        </div>
                        <div className="rounded-[22px] border border-white/10 bg-[#151515] px-4 py-4" data-testid="signature-district-signal-metric-alpha-score">
                          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]">District Match Score</p>
                          <p className="mt-3 text-3xl font-semibold text-[#F9F8F6]">{formatAlphaScore(selectedDistrictDetail.districtAlphaScore ?? selectedDistrictDetail.resolvedDistrictAlphaScore ?? Number(buildDistrictAlphaScore(selectedDistrictDetail)))}</p>
                        </div>
                      </div>
                      <p className="mt-4 text-sm leading-7 text-[#F2ECE3]" data-testid="signature-district-signal-insight-copy" style={{ color: "#F2ECE3", WebkitTextFillColor: "#F2ECE3" }}>{signalInsightCopy}</p>
                    </>
                  ) : (
                    <div className="rounded-[22px] border border-[#D4AF37]/16 bg-[#151515] px-4 py-4 text-sm leading-7 text-[#F2ECE3]" data-testid="signature-district-signal-panel-empty-state" style={{ color: "#F2ECE3", WebkitTextFillColor: "#F2ECE3" }}>
                      Click a district above to reveal its current yield, price gap, and district match score.
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            <div className="rounded-[30px] border border-[#DDD4C4] bg-[#F9F8F6] px-5 py-5 shadow-[0_20px_50px_rgba(44,44,44,0.06)]" data-testid="signature-district-leaderboard-card">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#2C2C2C] text-[#D4AF37]" data-testid="signature-district-leaderboard-icon">
                  <Trophy className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8B6E3D]">District watchlist</p>
                  <h3 className="mt-2 font-[Cormorant_Garamond,serif] text-3xl font-bold leading-none text-[#2C2C2C]" data-testid="signature-district-leaderboard-heading">Top 5 districts to explore next</h3>
                </div>
              </div>

              <div className="mt-5 space-y-3" data-testid="signature-district-leaderboard-list">
                {districtLeaderboard.map((district, index) => (
                  <button
                    className={`flex w-full items-center justify-between gap-4 rounded-[22px] border px-4 py-4 text-left transition ${highlightedDistrictCode === district.districtCode ? "signature-leaderboard-row-active border-[#D4AF37]/45 bg-[#fff7e2]" : index === 0 ? "border-[#D4AF37]/35 bg-[#fff7e2]" : "border-[#E4DED4] bg-white hover:border-[#D4AF37]/25"}`}
                    data-testid={`signature-district-leaderboard-row-${district.districtCode.toLowerCase()}`}
                    key={district.districtCode}
                    onClick={() => handleDistrictSelect(district.districtCode)}
                    type="button"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#2C2C2C] text-xs font-bold text-[#F9F8F6]" data-testid={`signature-district-leaderboard-rank-${district.districtCode.toLowerCase()}`}>{index + 1}</span>
                        <p className="text-lg font-semibold text-[#1F1A11]" data-testid={`signature-district-leaderboard-code-${district.districtCode.toLowerCase()}`}>{district.districtCode}</p>
                        {index === 0 ? <span className="rounded-full bg-[#D4AF37] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#2C2C2C]" data-testid="signature-district-leaderboard-champion-pill">Champion</span> : null}
                      </div>
                      <p className="mt-2 text-sm leading-6 text-[#5D584F]" data-testid={`signature-district-leaderboard-copy-${district.districtCode.toLowerCase()}`}>
                        Avg Yield {formatPct(district.averageYield) || "—"} · {district.underValuedCount} standout homes · {district.listingCount} live listings
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8B6E3D]">Match Score</p>
                      <p className="mt-2 font-[Cormorant_Garamond,serif] text-4xl font-bold leading-none text-[#2C2C2C]" data-testid={`signature-district-leaderboard-score-${district.districtCode.toLowerCase()}`}>{formatAlphaScore(district.resolvedDistrictAlphaScore)}</p>
                    </div>
                  </button>
                ))}
              </div>

              <p className="mt-4 text-xs leading-6 text-[#6B6252]" data-testid="signature-district-leaderboard-footnote">Ranked from live district yield, pricing gap pressure, standout-home count, and the strongest overall watchlist score currently visible in {advisorFirstName}&apos;s portfolio.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell relative z-20 py-6" data-testid="signature-listing-tools-section">
        <section className="rounded-[32px] border border-[#D4AF37]/20 bg-[linear-gradient(135deg,#fff9ef_0%,#f7efe2_56%,#efe2c8_100%)] p-6 shadow-[0_18px_50px_rgba(90,75,53,0.08)] md:p-7" data-testid="signature-listing-tools-panel">
          <div className="rounded-[28px] border border-[#D4AF37]/18 bg-white/78 px-5 py-6 shadow-[0_18px_42px_rgba(90,75,53,0.05)] md:px-6" data-testid="signature-agent-trust-listing-features-copy-block">
            <div className="max-w-4xl space-y-3">
              <p className="text-base font-semibold text-[#1f1a11]" data-testid="signature-agent-trust-listing-copy-line-1">
                Go from browsing a listing to booking a viewing in under 60 seconds.
              </p>
              <p className="text-sm leading-7 text-[#4d4437]" data-testid="signature-agent-trust-listing-copy-line-2">
                The cleanest data in Singapore&mdash;no fake listings, no expired ads, just real gems.
              </p>
              <p className="text-sm leading-7 text-[#4d4437]" data-testid="signature-agent-trust-listing-copy-line-3">
                Everything you need to buy with confidence, all in one place.
              </p>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-4" data-testid="signature-agent-trust-listing-features-heading-row">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#8B6E3D]" data-testid="signature-agent-trust-listing-features-kicker">Client support tools</p>
                <h3 className="mt-2 text-xl font-semibold text-[#1f1a11]" data-testid="signature-agent-trust-listing-features-heading">Everything built into the browsing journey</h3>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-[#D4AF37]/18 bg-white/80 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#8B6E3D]" data-testid="signature-agent-trust-listing-features-count">
                18 client-friendly tools
                </span>
                {isCompactFeatureSections ? (
                  <button className="outline-button !px-4 !py-2 !text-xs" data-testid="signature-agent-trust-listing-features-toggle" onClick={() => setIsListingToolsExpanded((current) => !current)} type="button">
                    {isListingToolsExpanded ? <ChevronUp className="mr-2 h-4 w-4" /> : <ChevronDown className="mr-2 h-4 w-4" />}
                    {isListingToolsExpanded ? "Collapse" : "Expand"}
                  </button>
                ) : null}
              </div>
            </div>

            {!isCompactFeatureSections || isListingToolsExpanded ? (
              <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="signature-agent-trust-listing-features-grid">
                {SIGNATURE_LISTING_PAGE_FEATURES.slice(0, 6).map((feature, index) => (
                  <div className="rounded-[24px] border border-[#D4AF37]/14 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(249,245,236,0.96))] px-4 py-4" data-testid={`signature-agent-trust-listing-feature-${index + 1}`} key={feature.title}>
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8B6E3D]">Listing Feature {String(index + 1).padStart(2, "0")}</p>
                    <h3 className="mt-3 text-base font-semibold leading-6 text-[#1f1a11]">{feature.title}</h3>
                    <p className="mt-2 text-xs leading-6 text-[#5D584F]">{feature.body}</p>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </section>
      </section>

      <section className="section-shell relative z-20 py-6" data-testid="signature-alpha-logic-strip-section">
        <div className="overflow-hidden rounded-[34px] border border-[#DDD4C4] bg-white shadow-[0_24px_70px_rgba(44,44,44,0.08)]">
          <div className="grid gap-px bg-[#E4DED4] md:grid-cols-3">
            {alphaLogicStats.map((item) => {
              const Icon = item.icon;
              return (
                <div className="bg-[#F9F8F6] px-6 py-7 lg:px-8" data-testid={`signature-alpha-logic-card-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={item.label}>
                  <div className="flex items-center gap-3 text-[#1f1a11]"><Icon className="h-5 w-5 text-[#8B6E3D]" /><p className="signature-intelligence-label text-[11px] font-bold uppercase tracking-[0.24em] text-[#1f1a11]">{item.label}</p></div>
                  <p className="mt-5 font-[Cormorant_Garamond,serif] text-4xl font-bold leading-none text-[#16120b] [text-shadow:0_2px_0_rgba(255,255,255,0.55)]" data-testid={`signature-alpha-logic-value-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{item.value}</p>
                  <p className="mt-4 text-sm font-medium leading-7 text-[#3f3420]">{item.detail}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-shell py-6" data-testid="signature-search-section">
        <div className="max-w-6xl">
          <p className="signature-kicker-label text-xs font-bold uppercase tracking-[0.28em] text-[#8B6E3D]">Browse live listings</p>
          <h2 className="mt-4 font-[Cormorant_Garamond,serif] text-4xl font-bold leading-none text-[#2C2C2C] sm:text-5xl">Search homes by district, tenure, and property type.</h2>
          <p className="mt-4 max-w-3xl text-base leading-8 text-[#5D584F]">Use the filters below to focus your shortlist, then message {advisorFirstName} when you want a recommendation or viewing slot.</p>
        </div>
        <div className="mt-10">
          <SearchBar filters={filters} onApply={handleSearch} onFiltersChange={onFiltersChange} onReset={onResetFilters} options={options} testIdPrefix="signature-homepage-search" />
        </div>
      </section>

      <section className="section-shell py-12" data-testid="signature-listing-grid-section">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="signature-kicker-label text-xs font-bold uppercase tracking-[0.28em] text-[#8B6E3D]">Featured homes</p>
            <h2 className="mt-4 font-[Cormorant_Garamond,serif] text-4xl font-bold leading-none text-[#2C2C2C] sm:text-5xl">Homes worth seeing first.</h2>
          </div>
          <Link className="outline-button" data-testid="signature-grid-see-all-link" to={buildBrandedPath("/listings")}>See all inventory</Link>
        </div>

        {shortlist.count ? (
          <div className="mt-8">
            <ShortlistStudioPanel
              buildListingPath={(listingId) => buildBrandedPath(`/listings/${listingId}`)}
              compact
              count={shortlist.count}
              items={shortlist.items}
              onClearShortlist={() => {
                shortlist.clearAll();
                toast.success("Shortlist cleared");
              }}
              onCopyShareLink={async () => {
                await shortlist.copyShareLink();
                toast.success("Shortlist link copied");
              }}
              onExportCsv={() => {
                shortlist.exportCsv(`${advisorFirstName.toLowerCase()}-shortlist`);
                toast.success("Shortlist CSV exported");
              }}
              onNativeShare={async () => {
                await shortlist.shareNatively(`${advisorFirstName}'s shortlist`);
              }}
              onRemoveListing={(listingId) => {
                shortlist.removeListing(listingId);
                toast.success("Listing removed from shortlist");
              }}
              supportsNativeShare={shortlist.supportsNativeShare}
              testIdPrefix="signature-shortlist"
            />
          </div>
        ) : null}

        {filteredEditorialListings.length ? (
          <div className="mt-10 grid gap-12 lg:grid-cols-2 xl:gap-x-14 xl:gap-y-16">
            {filteredEditorialListings.slice(0, 4).map((listing) => (
              <SignatureEditorialListingCard advisorFirstName={advisorFirstName} buildBrandedPath={buildBrandedPath} getWhatsAppHref={getWhatsAppHref} key={listing.id} leadFormPath={leadFormPath} listing={listing} />
            ))}
          </div>
        ) : (
          <div className="mt-10 rounded-[34px] border border-[#DDD4C4] bg-[#F9F8F6] px-8 py-12 shadow-[0_18px_50px_rgba(44,44,44,0.06)]" data-testid="signature-listing-empty-state">
            <p className="signature-kicker-label text-xs font-bold uppercase tracking-[0.24em] text-[#8B6E3D]">{activeDistrict ? `${activeDistrict} filter active` : "Portfolio Sync Pending"}</p>
            <h3 className="mt-4 font-[Cormorant_Garamond,serif] text-4xl font-bold leading-none text-[#2C2C2C]">{activeDistrict ? `No live homes have synced into ${activeDistrict} yet.` : `${advisorFirstName}'s live inventory will appear here the moment sync completes.`}</h3>
            <p className="mt-5 max-w-3xl text-base leading-8 text-[#5D584F]">{activeDistrict ? `${activeDistrict} is already connected to the filtering layer. As soon as homes land from sync, this page will narrow straight to the district.` : "This featured homes grid is ready to surface the strongest options the moment new portfolio data arrives."}</p>
          </div>
        )}
      </section>

      <section className="section-shell py-16" data-testid="signature-advisor-cta-section">
        <div className="rounded-[38px] bg-[#2C2C2C] px-8 py-12 text-[#F9F8F6] shadow-[0_28px_80px_rgba(44,44,44,0.18)] md:px-12 lg:px-14" data-testid="signature-advisor-cta-card">
          <p className="signature-kicker-label text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37]" style={{ color: "#D4AF37", textShadow: "0 0 16px rgba(212, 175, 55, 0.12)" }}>Need a second opinion?</p>
          <div className="mt-6 grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
            <div>
              <h2 className="font-[Cormorant_Garamond,serif] text-4xl font-bold leading-none text-[#F9F8F6] sm:text-5xl" data-testid="signature-advisor-cta-heading" style={{ color: "#F9F8F6", textShadow: "0 8px 26px rgba(0, 0, 0, 0.16)" }}>Get quick answers before you book the viewing.</h2>
              <p className="mt-5 max-w-3xl text-base leading-8 text-[#F2ECE3]" data-testid="signature-advisor-cta-copy" style={{ color: "#F2ECE3" }}>
                Ask about nearby options, estimated taxes, rental yield, or whether a specific home fits your budget. If you prefer, jump straight to WhatsApp with {advisorFirstName}.
              </p>
            </div>
            <div className="grid gap-3 sm:max-w-sm sm:justify-self-end">
              <button className="gold-button !justify-center" data-testid="signature-advisor-open-button" onClick={() => window.dispatchEvent(new CustomEvent("signature-advisor-open"))} type="button">
                <Bot className="mr-2 h-4 w-4" /> Open instant help
              </button>
              <a className="outline-button !justify-center !border-white/20 !text-[#F9F8F6] hover:!bg-white/10" data-testid="signature-advisor-whatsapp-link" href={getWhatsAppHref(`Hello ${advisorFirstName}, I would like help choosing a property.`)} rel="noreferrer" target="_blank">
                WhatsApp {advisorFirstName} directly
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};